import React, { useState, useEffect } from "react";
import styled from "styled-components";
import colors from "styles/colors";
import { ThreeDotsVertical, ExclamationCircleFill } from "styled-icons/bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import rcrmActions from "modules/rcrm/actions";
import googleActions from "modules/google/actions";
import googleSelectors from "modules/google/selectors";
import dateFormatter from "modules/utils/DateFormatter";
import { TextHeader } from "components/Texts";
import Button from "components/Buttons";
import SettingModal from "components/gads/SettingModal";
import TargetCard from "components/TargetCard";
import ToggleSwitch from "components/ToggleSwitch";
import { Text } from "components/Texts";
import Toast from "components/Toast";
import Modal from "components/Modal";
import LeadsConnectionErrorMessage from "components/LeadsConnectionErrorMessage";
import decodeHTMLEntities from "modules/utils/DecodeHTMLEntities";
import mainSelectors from "modules/main/selectors";

const LeadForms = () => {
  const rmpToken = sessionStorage.getItem("rmp_token");
  const { REACT_APP_GOOGLE_ADS_SETUP_MANUAL_URL, REACT_APP_RMP_RMP_API_URL, REACT_APP_RMP_APP_ID } = process.env;
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState("googleSheets");
  const [editStatus, setEditStatus] = useState("");
  const [modalSettingDisplay, setModalSettingDisplay] = useState("hide");
  const googleAllLeadFormSettingsResult = useSelector((state) =>
    googleSelectors.getAllGoogleLeadFormSettingsResult(state),
  );
  const availableForm = useSelector((state) => googleSelectors.getGoogleAdsLeadFormResult(state));
  const businessInfoResult = useSelector((state) => googleSelectors.getBusinessInfoResult(state));
  const RcrmAppAvailableResult = useSelector((state) => mainSelectors.checkAppAvailableResult(state));
  const setBusinessAdwordsIdResult = useSelector((state) => googleSelectors.setBusinessAdwordsIdResult(state));
  const ggConnectionLoading = useSelector((state) => googleSelectors.setGGConnectionLoadingResult(state));
  const [modalStatusDisplay, setModalStatusDisplay] = useState("hide");
  const [statusLoading, setStatusLoading] = useState(false);
  const [isOptionsVisible, setOptionsVisible] = useState("");
  const [userStatus, setUserStatus] = useState();
  const [toggleStatus, setToggleStatus] = useState({});
  const [leadFormId, setLeadFormId] = useState();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showFailedToast, setShowFailedToast] = useState(false);
  const [modalConfirmDeleteDisplay, setModalConfirmDeleteDisplay] = useState("hide");
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [showDeleteFailedToast, setShowDeleteFailedToast] = useState(false);
  const menu = "google_lead_form";
  const selectedMenu = sessionStorage.getItem("selectedMenu");

  useEffect(() => {
    if (
      !businessInfoResult.data.adwords_id ||
      businessInfoResult.data.adwords_id === "" ||
      (setBusinessAdwordsIdResult.adwordsId &&
        setBusinessAdwordsIdResult.adwordsId !== businessInfoResult.data.adwords_id)
    ) {
      dispatch(
        googleActions.getBusinessInfo(rmpToken, {
          appId: REACT_APP_RMP_APP_ID,
        }),
      );
    }
  }, [businessInfoResult, setBusinessAdwordsIdResult]);

  useEffect(() => {
    async function fetchData() {
      const leadAll = dispatch(
        googleActions.getAllGoogleLeadFormSettings({
          token: rmpToken,
          appReserve: currentTab,
        }),
      );
      return leadAll;
    }

    /**
     * set default of connection status
     */
    fetchData().then((data) => {
      if (data.result === 0 && data.msg === "GET_DATA_COMPLETED") {
        const initialStatus = data.data.reduce((acc, item) => {
          acc[item.id] = item.user_status === "active";
          return acc;
        }, {});
        handleToggleStatus(initialStatus);
      }
    });
  }, [currentTab, selectedMenu]);

  useEffect(() => {
    dispatch(
      googleActions.getGoogleAdsLeadForm({
        token: rmpToken,
        appReserve: currentTab,
      }),
    );
  }, [currentTab]);

  /**
   * get all connection data each tab
   * @param {*} tab
   */
  const getAllLeadFormSettings = async (tab) => {
    await dispatch(
      googleActions.getAllGoogleLeadFormSettings({
        token: rmpToken,
        appReserve: tab,
      }),
    );
    await dispatch(googleActions.setGGConnectionLoading(false));
  };

  /**
   * set current tab
   * @param {string} tab
   */
  const changeCurrentTab = async (tab) => {
    await dispatch(googleActions.setGGConnectionLoading(true));
    setCurrentTab(tab);
    getAllLeadFormSettings(tab);
  };

  const handleToggleStatus = (value) => {
    if (menu === selectedMenu) {
      setToggleStatus(value);
    }
  };

  /**
   * get google ads data (left side)
   * add loading state before finish get data
   * @param {*} params
   */
  const getGoogleAdsLeadForm = async (params) => {
    await dispatch(googleActions.setGoogleAdsLoading(true));
    await dispatch(
      googleActions.getGoogleLeadFormSetting({
        token: rmpToken,
        id: params === null ? null : params.leadFormId,
      }),
    );

    const googleAdsSetting = await dispatch(
      googleActions.getGoogleAdsSetting({
        token: rmpToken,
        id: params === null ? null : params.leadFormId,
      }),
    );

    const adsLeadForm = await dispatch(
      googleActions.getGoogleAdsLeadForm({
        token: rmpToken,
        appReserve: currentTab,
      }),
    );

    if (adsLeadForm.result === 0 && adsLeadForm.msg === "GET_DATA_COMPLETED") {
      let googleAdsFormSelectType = "all";
      let checkedFormItems = [];

      if (adsLeadForm.formStatus == "SOMEUSED" || adsLeadForm.formStatus == "ALLUSED") {
        googleAdsFormSelectType = "select";
      }

      /**
       * if have data in database then set value
       */
      if (googleAdsSetting.result === 0 && googleAdsSetting.msg === "GET_DATA_COMPLETED") {
        googleAdsFormSelectType = googleAdsSetting.data.google_form_setting;
        checkedFormItems = googleAdsSetting.data.leadForm
          .filter((item) => item.status === "active")
          .map((item) => item.asset_id);
        if (businessInfoResult.data.adwords_id.replace(/-/g, "") != googleAdsSetting.data.google_adword_id) {
          if (adsLeadForm.formStatus == "NOTALLUSED") {
            googleAdsFormSelectType = "all";
            checkedFormItems = [];
          } else if (adsLeadForm.formStatus == "SOMEUSED" || adsLeadForm.formStatus == "ALLUSED") {
            googleAdsFormSelectType = "select";
            checkedFormItems = [];
          }
        }
      }

      localStorage.setItem("googleAdsFormSelectType", googleAdsFormSelectType);
      await dispatch(googleActions.changeGoogleAdsFormSelectType(googleAdsFormSelectType));
      await dispatch(googleActions.setCheckedGoogleAdsFormItems(checkedFormItems));
      await dispatch(googleActions.setGoogleAdsLoading(false));
    } else {
      let googleAdsFormSelectType = null;
      let checkedFormItems = null;
      localStorage.setItem("googleAdsFormSelectType", googleAdsFormSelectType);
      await dispatch(googleActions.changeGoogleAdsFormSelectType(googleAdsFormSelectType));
      await dispatch(googleActions.setCheckedGoogleAdsFormItems(checkedFormItems));
      await dispatch(googleActions.setGoogleAdsLoading(false));
    }
  };

  /**
   * reset state value before add connection
   */
  const resetGoogleAds = async () => {
    await dispatch(googleActions.setValidateGoogleLeadForm({ result: false }));
    await dispatch(googleActions.changeGoogleAdsFormSelectType(""));
    await dispatch(googleActions.setCheckedGoogleAdsFormItems(""));
  };

  /**
   * reset state value before add connection
   */
  const resetGoogle = async () => {
    await dispatch(googleActions.setGoogleLeadGoogleConnected(false));
    await dispatch(
      googleActions.getGoogleExchangeToken({
        token: rmpToken,
        code: null,
      }),
    );
    await dispatch(
      googleActions.getGoogleSheetsSetting({
        token: rmpToken,
        id: null,
      }),
    );
    await dispatch(googleActions.setValidateGGLeadsGoogleSheetsUrl({ result: false }));
    await dispatch(googleActions.setGoogleSheetsError(false));
  };

  /**
   * reset state value before add connection
   */
  const resetCRM = async () => {
    await dispatch(rcrmActions.setGateItem({ gateId: null, gateName: null }));
  };

  /**
   * reset state value before edit connection
   */
  const resetBeforeEdit = async () => {
    await dispatch(googleActions.setGoogleSheetsError(false));
    await dispatch(googleActions.setGoogleLeadGoogleConnected(false));
    await dispatch(googleActions.setGoogleLeadGoogleDisconnected(false));
    await dispatch(
      googleActions.getGoogleExchangeToken({
        token: rmpToken,
        code: null,
      }),
    );
  };

  /**
   * add connection case -> params = null and can't get data, so ui will show signin button
   * but edit connection case can get R-CRM data if have in database
   */
  const getLeadTarget = async (leadFormId) => {
    await dispatch(rcrmActions.setCRMLoading(true));
    const target = await dispatch(
      rcrmActions.getLeadTarget({
        token: rmpToken,
      }),
    );

    const crmSetting = await dispatch(
      googleActions.getGoogleLeadCRMSetting({
        token: rmpToken,
        id: leadFormId === null ? null : leadFormId,
      }),
    );

    if (crmSetting.result === 0 && crmSetting.msg === "GET_DATA_COMPLETED") {
      let gateIdData = crmSetting.data.gateData === null ? null : crmSetting.data.gateData.gate_id;
      let gateNameData = crmSetting.data.gateData === null ? null : crmSetting.data.gateData.gate_name;
      await dispatch(rcrmActions.setGateItem({ gateId: gateIdData, gateName: gateNameData }));
      await dispatch(rcrmActions.setCRMLoading(false));
    } else {
      if (target.result === 0 && target.msg === "GET_DATA_COMPLETED" && target.gates) {
        await dispatch(
          rcrmActions.setGateItem({ gateId: target.gates[0].gate_id, gateName: target.gates[0].gate_name }),
        );
      }
      await dispatch(rcrmActions.setCRMLoading(false));
    }
  };

  /**
   * add connection case -> params = null and can't get data, so ui will show signin button
   * but edit connection case can get google data if have in database
   */
  const getGoogleAccount = async (params) => {
    await dispatch(googleActions.setGoogleLoading(true));
    await dispatch(googleActions.setGoogleSheetsLoading(true));
    if (params !== null) {
      if (params.googleStatus === "connected") {
        await dispatch(googleActions.setGoogleLeadGoogleConnected(true));
      }
    }

    const sheetsSetting = await dispatch(
      googleActions.getGoogleSheetsSetting({
        token: rmpToken,
        id: params === null ? null : params.leadFormId,
      }),
    );

    if (sheetsSetting.result === 0 && sheetsSetting.msg === "GET_DATA_COMPLETED") {
      await dispatch(googleActions.setGoogleLoading(false));
    } else {
      await dispatch(googleActions.setGoogleLoading(false));
    }

    const googleAccountData = await dispatch(
      googleActions.getGoogleAccount({
        token: rmpToken,
        googleAccount: params === null ? params : params.googleAccount,
      }),
    );

    if (googleAccountData.result === 0 && googleAccountData.data != null) {
      if (params !== null) {
        dispatch(googleActions.changeGoogleLeadSheetsUrlInput(params.spreadsheetUrl));
      }
      const sheets = await dispatch(
        googleActions.getGoogleSheetsList({
          token: rmpToken,
          refreshToken: googleAccountData.data.refresh_token,
          googleAccount: googleAccountData.data.google_account,
          spreadsheetUrl: params === null ? params : params.spreadsheetUrl,
        }),
      );

      /**
       * update connection status if have error (google_lead_form_setting)
       */
      if (sheets.result === 0 && sheets.data != null) {
        // do nothing
      } else {
        if (params.googleStatus == "connected") {
          await dispatch(googleActions.setGoogleSheetsError(true));
          await dispatch(
            googleActions.updateGGLeadFormSettingErrorStatus({
              token: rmpToken,
              googleLeadSettingId: params.leadFormId,
              connectedApp: "googleSheets",
              errorMessage: "gsheet_not_found",
            }),
          );
          await getAllLeadFormSettings("googleSheets");
        }
      }
      await dispatch(googleActions.setGoogleSheetsLoading(false));
    } else {
      await dispatch(googleActions.setGoogleSheetsLoading(false));
    }
  };

  /**
   * /**
   * button for add or edit setting
   * @param {*} currentTab
   * @param {*} status
   * @param {*} leadFormSetting
   */
  const handleSettingModal = (currentTab, status, leadFormSetting) => {
    dispatch(googleActions.setDuplicateGoogleForm({ duplicate: null }));
    dispatch(rcrmActions.setValidateGate({ result: false }));
    if (status === "add") {
      resetGoogleAds();
      setEditStatus("add");
      setModalSettingDisplay("show");
      getGoogleAdsLeadForm(null);
      if (currentTab === "googleSheets") {
        resetGoogle();
        getGoogleAccount(null);
      } else if (currentTab === "rcrm") {
        resetCRM();
        getLeadTarget(null);
      }
    } else if (status === "edit") {
      resetBeforeEdit();
      const params = {
        googleStatus: leadFormSetting.google_sheets_status,
        googleMessageError: leadFormSetting.google_sheets_message_error,
        googleAccount: leadFormSetting.googleSheets == null ? null : leadFormSetting.googleSheets.google_account,
        spreadsheetUrl: leadFormSetting.googleSheets == null ? null : leadFormSetting.googleSheets.file.spreadsheet_url,
        leadFormId: leadFormSetting.id,
      };
      localStorage.setItem("leadFormId", params.leadFormId);
      dispatch(googleActions.setValidateGoogleLeadForm({ result: false }));
      dispatch(googleActions.setValidateGGLeadsGoogleSheetsUrl({ result: false }));
      setEditStatus("edit");
      setModalSettingDisplay("show");
      getGoogleAdsLeadForm(params);
      if (currentTab === "googleSheets") {
        getGoogleAccount(params);
      } else if (currentTab === "rcrm") {
        getLeadTarget(params.leadFormId);
      }
    }
  };

  /**------------click more icon (start)---------------*/

  /** menu delete */
  const handleStatusCloseConfirmDeleteModal = () => {
    setModalConfirmDeleteDisplay("hide");
  };

  const archiveLeadFormSetting = async () => {
    const archiveSetting = await dispatch(
      googleActions.archiveGoogleLeadFormSetting({
        token: rmpToken,
        id: leadFormId,
      }),
    );

    setModalConfirmDeleteDisplay("hide");
    getAllLeadFormSettings(currentTab);
    await dispatch(
      googleActions.getGoogleAdsLeadForm({
        token: rmpToken,
        appReserve: currentTab,
      }),
    );
    archiveSetting.result === 0 ? setShowDeleteSuccessToast(true) : setShowDeleteFailedToast(true);
  };

  const modalConfirmDeleteContent = () => (
    <ModalContentContainer>
      <div className="info-icon">&#9432;</div>
      <div>{t("__confirm_delete")}</div>
    </ModalContentContainer>
  );

  const modalConfirmDeleteFooter = () => (
    <ModalButtonContainer>
      <Button type="secondary" onClick={handleStatusCloseConfirmDeleteModal}>
        {t("__cancel")}
      </Button>
      <Button type="loading" onClick={() => archiveLeadFormSetting()} loading={statusLoading}>
        {t("__confirm")}
      </Button>
    </ModalButtonContainer>
  );

  const deleteSuccessToastCallback = (value) => {
    setShowDeleteSuccessToast(value);
  };

  const deleteFailedToastCallback = (value) => {
    setShowDeleteFailedToast(value);
  };
  /** menu delete */

  const handleEditModal = (status, leadFormSetting) => {
    handleSettingModal(currentTab, status, leadFormSetting);
  };

  const onDropdownItemSelected = (item, leadFormSetting) => {
    handleClickMoreOption(leadFormSetting.id);
    if (item.status === "edit") {
      handleEditModal(item.status, leadFormSetting);
    } else if (item.status === "delete") {
      setLeadFormId(leadFormSetting.id);
      setModalConfirmDeleteDisplay("show");
    }
    setOptionsVisible("");
  };

  const handleClickMoreOption = (id) => {
    setOptionsVisible(id);
  };

  const DropdownItem = ({ item, index, onClick }) => {
    return (
      <StyledItem onClick={() => onClick(item, index)}>
        <MenuIcon src={item.icon} />
        <Text size="12px" color="#999">
          {item.title}
        </Text>
      </StyledItem>
    );
  };

  let items = [
    { status: "edit", title: t("__fbleads_edit_connection"), icon: "/images/edit.svg" },
    { status: "delete", title: t("__delete"), icon: "/images/delete.svg" },
  ];
  const viewItems = (leadFormSetting) =>
    items.map((item, index) => (
      <DropdownItem
        key={index}
        item={item}
        index={index}
        onClick={() => onDropdownItemSelected(item, leadFormSetting)}
      ></DropdownItem>
    ));
  /**------------click more icon (end)---------------*/

  /** toggle */

  const handleStatusCloseModal = () => {
    setModalStatusDisplay("hide");
  };

  const switchGoogleLeadFormStatus = async (params) => {
    const switchLeadForm = await dispatch(
      googleActions.switchGoogleLeadFormStatus({
        token: rmpToken,
        googleLeadSettingId: params.googleLeadSettingId,
        status: params.status,
      }),
    );
    if (switchLeadForm.result === 0 && switchLeadForm.msg === "UPDATE_DATA_COMPLETED") {
      setShowSuccessToast(true);
    } else {
      setShowFailedToast(true);
    }
    setModalStatusDisplay("hide");
    setStatusLoading(false);
  };

  /**
   * if click confirm to active or inactive connection status
   */
  const handleStatus = () => {
    setStatusLoading(true);
    handleToggleStatus((prevStatuses) => ({
      ...prevStatuses,
      [leadFormId]: !prevStatuses[leadFormId],
    }));
    const params = {
      googleLeadSettingId: leadFormId,
      status: toggleStatus[leadFormId] === false || toggleStatus[leadFormId] === undefined ? "active" : "inactive",
    };
    switchGoogleLeadFormStatus(params);
  };

  const handleOnChange = () => {
    console.log("handleOnChange");
  };

  /**
   * action after click toggle switch
   * @param {*} id
   */
  const handleChangeStatus = (id) => {
    setLeadFormId(id);
    let newStatus = toggleStatus[id] === true ? "active" : "inactive";
    setUserStatus(newStatus);
    setModalStatusDisplay("show");
  };

  /**
   * modal content to confirm active or inactive connection status
   */
  const modalStatusContent = () => (
    <ModalContentContainer>
      <div className="info-icon">&#9432;</div>
      <div>
        {userStatus === "active" ? t("__fbleads_change_status_to_inactive") : t("__fbleads_change_status_to_active")}
      </div>
    </ModalContentContainer>
  );

  /**
   * modal footer to confirm active or inactive connection status
   */
  const modalStatusFooter = () => (
    <ModalButtonContainer>
      <Button type="secondary" onClick={handleStatusCloseModal}>
        {t("__cancel")}
      </Button>
      <Button type="loading" onClick={() => handleStatus()} loading={statusLoading}>
        {t("__confirm")}
      </Button>
    </ModalButtonContainer>
  );

  const successToastCallback = (value) => {
    setShowSuccessToast(value);
  };

  const failedToastCallback = (value) => {
    setShowFailedToast(value);
  };

  /** toggle */

  const ToggleSwitchComponent = ({ item, index }) => {
    const isDisabled =
      (currentTab === "googleSheets" &&
        (item.google_adword_status !== "connected" || item.google_sheets_status !== "connected")) ||
      (currentTab === "rcrm" && (item.google_adword_status !== "connected" || item.rcrm_status !== "connected")) ||
      item.googleAds.google_adword_id != businessInfoResult.data.adwords_id.replace(/-/g, "");
    return (
      <ToggleSwitch
        key={index}
        value="on"
        checked={toggleStatus[item.id] || false}
        onChange={handleOnChange}
        onClick={() => handleChangeStatus(item.id)}
        width="50px"
        height="24px"
        bfwidth="16px"
        bfheight="16px"
        disabled={isDisabled}
      />
    );
  };

  const IconComponent = ({ item }) => (
    <IconContainer>
      <StyleThreeDotsVertical size="16" onClick={() => handleClickMoreOption(item.id)} />
      <ItemsContainer
        isVisible={isOptionsVisible === item.id ? true : false}
        position={{ top: "30px", right: "0px" }}
        minWidth="150px"
      >
        {viewItems(item)}
      </ItemsContainer>
      {isOptionsVisible === item.id && <Backdrop onClick={() => setOptionsVisible("")} />}
    </IconContainer>
  );

  const AccountContent = ({ adWordId, adWordName }) => (
    <AccountText>
      <div className="google-ads-name">{adWordName}</div>
      <div className="google-ads-id">{adWordId}</div>
    </AccountText>
  );

  const FormListComponent = ({ lead_form }) => (
    <FormListContainer>
      {lead_form.map(
        (form, index) =>
          form.status === "active" && (
            <li key={index} className="item-list">
              <span className="form-name">{form.asset_name}</span>
              <div className="form-id">
                {t("__form_id")}: {form.asset_id}
              </div>
            </li>
          ),
      )}
    </FormListContainer>
  );

  /**
   * get data to show in file card and file detail modal
   * @param {*} file
   * @param {*} status
   * @returns
   */
  const getFileData = (file, googleSheetsStatus) => {
    const getFile = {
      googleAccount: file.google_account,
      name: file.spreadsheet_name,
      googleStatus: googleSheetsStatus ?? "connected",
      fileLink: file.spreadsheet_url,
      folderType: file.drive_type,
      folderName: file.file_path,
      folderCount: file.folder_count,
      folderUrl: file.folder_url,
      updatedDate: file.last_modified_datetime,
      updatedByImage: file.last_modified_user_profile_image,
      updatedBy: file.last_modified_user,
      position: "card",
    };
    return getFile;
  };

  /**
   * get data to show in gate card
   * @param {*} rcrm
   * @returns
   */
  const getGateData = (rcrm) => {
    const getGate = {
      name: rcrm.gateData.gate_name,
      gateId: rcrm.gateData.gate_id,
      position: "card",
    };

    return getGate;
  };

  const Card = () => {
    const { result, appReserve, data, msg } = googleAllLeadFormSettingsResult;
    const isDataAvailable =
      result === 0 && msg === "GET_DATA_COMPLETED" && appReserve === currentTab && data.length > 0;

    const renderCard = (item, index) => (
      <MainCardContainer key={index} id={`connection_${item.id}`}>
        <CardBoxContainer
          ggAdsStatus={item.google_adword_status}
          ggStatus={item.google_sheets_status}
          crmStatus={item.rcrm_status}
          googleAdsChangeStatus={
            item.googleAds.google_adword_id != businessInfoResult.data.adwords_id.replace(/-/g, "")
          }
        >
          <CardBoxContent>
            <CardBoxHeader>
              <HeaderContainer>
                <HeaderAction>
                  <ToggleSwitchComponent item={item} index={index} />
                  <IconComponent item={item} />
                </HeaderAction>
              </HeaderContainer>
            </CardBoxHeader>
            <CardBoxBody>
              <GoogleAdsArea status={item.google_adword_status}>
                {item.google_adword_status != null && (
                  <>
                    <AccountArea>
                      <ErrorIconArea>
                        {item.google_adword_status == "error" ||
                          (item.googleAds.google_adword_id != businessInfoResult.data.adwords_id.replace(/-/g, "") && (
                            <>
                              <ErrorIcon
                                size="16"
                                color={colors.negative}
                                data-tip
                                data-for={"google-ads-error-" + item.id}
                              />
                              <ReactTooltip
                                id={"google-ads-error-" + item.id}
                                className="tooltip-style"
                                place="top"
                                type="dark"
                                effect="solid"
                                aria-haspopup="true"
                              >
                                <div>{t("__googleleads_google_error_change_ads_account")}</div>
                              </ReactTooltip>
                            </>
                          ))}
                      </ErrorIconArea>
                      <HeaderImage src="/images/google-ads.png" />
                      <AccountContent
                        adWordId={item.googleAds.google_adword_id}
                        adWordName={item.googleAds.google_adword_name}
                      />
                    </AccountArea>
                    <FormListComponent lead_form={item.googleAds.lead_form} />
                  </>
                )}
              </GoogleAdsArea>
              {currentTab === "googleSheets" ? (
                <FileArea status={item.google_sheets_status}>
                  {item.google_sheets_status != null ? (
                    <>
                      <ErrorIconTargetArea>
                        {item.google_sheets_status == "error" && (
                          <>
                            <ErrorIcon
                              size="16"
                              color={colors.negative}
                              data-tip
                              data-for={"gsheets-error-" + item.id}
                            />
                            <ReactTooltip
                              id={"gsheets-error-" + item.id}
                              className="tooltip-style"
                              place="top"
                              type="dark"
                              effect="solid"
                              aria-haspopup="true"
                            >
                              <LeadsConnectionErrorMessage
                                app="google"
                                errorMessage={item.google_sheets_message_error}
                              />
                            </ReactTooltip>
                          </>
                        )}
                      </ErrorIconTargetArea>
                      <TargetCard
                        type="googleSheets"
                        data={getFileData(item.googleSheets.file, item.google_sheets_status)}
                        leadFormId={item.id}
                        leadType="google"
                      />
                    </>
                  ) : (
                    <ConnectButtonContainer>
                      <Button type="secondary" onClick={() => handleSettingModal(currentTab, "edit", item)}>
                        {t("__fbleads_google_connect")}
                      </Button>
                    </ConnectButtonContainer>
                  )}
                </FileArea>
              ) : (
                currentTab === "rcrm" && (
                  <GateArea status={item.rcrm_status}>
                    <ErrorIconTargetArea>
                      {item.rcrm_status == "error" && (
                        <>
                          <ErrorIcon size="16" color={colors.negative} data-tip data-for={"rcrm-error-" + item.id} />
                          <ReactTooltip
                            id={"rcrm-error-" + item.id}
                            className="tooltip-style"
                            place="top"
                            type="dark"
                            effect="solid"
                            aria-haspopup="true"
                          >
                            <LeadsConnectionErrorMessage app="rcrm" errorMessage={item.rcrm_message_error} />
                          </ReactTooltip>
                        </>
                      )}
                    </ErrorIconTargetArea>
                    <TargetCard type="rcrm" data={getGateData(item.rcrm)} leadFormId={item.id} leadType="google" />
                  </GateArea>
                )
              )}
            </CardBoxBody>
            <CardBoxFooter>
              <FooterContainer>
                <LastEditedTime>
                  {t("__edit_time")}{" "}
                  {dateFormatter.formatDatetimeFull(
                    item.update_date === null ? item.create_date : item.update_date,
                    sessionStorage.getItem("lang"),
                  )}
                </LastEditedTime>
                <LastEditedBy>
                  <span className="edit-by">{t("__by")}</span>
                  <ProfileImage src={REACT_APP_RMP_RMP_API_URL + "/v1/user/photo/" + item.nexus_user_id} />
                  <LastEditedName>
                    {decodeHTMLEntities(item.update_user === null ? item.create_user : item.update_user)}
                  </LastEditedName>
                </LastEditedBy>
              </FooterContainer>
            </CardBoxFooter>
          </CardBoxContent>
        </CardBoxContainer>
        {(item.google_adword_status == "error" ||
          item.google_sheets_status == "error" ||
          item.rcrm_status == "error" ||
          item.googleAds.google_adword_id != businessInfoResult.data.adwords_id.replace(/-/g, "")) && (
          <ErrorArea>
            <div className="error-message">{t("__connection_problem")}</div>
            <div className="error-edit-link" onClick={() => handleSettingModal(currentTab, "edit", item)}>
              {t("__edit")}
            </div>
          </ErrorArea>
        )}
      </MainCardContainer>
    );

    return isDataAvailable && !ggConnectionLoading.data.loading ? (
      data.map(renderCard)
    ) : (data !== null && result != 1 && msg !== "NO_DATA") || ggConnectionLoading.data.loading ? (
      <LoadingArea>
        <StyledLoadingIcon />
      </LoadingArea>
    ) : null;
  };

  return (
    <>
      <LeadFormContainer>
        <SetupHeaderContainer>
          <SetupTextHeader>{t("__googleleads_title")}</SetupTextHeader>
          <StyledManualLink>
            <StyledLink target="_blank" rel="noopener noreferrer" href={REACT_APP_GOOGLE_ADS_SETUP_MANUAL_URL}>
              <HelpIcon src="/images/help_outline.svg" />
              {t("__ads_setup_manual_link")}
            </StyledLink>
          </StyledManualLink>
        </SetupHeaderContainer>
        <TextHeaderDescription>{t("__googleleads_title_description")}</TextHeaderDescription>
        <Tabs>
          <TabPanel
            className={currentTab === "googleSheets" ? "selected" : ""}
            onClick={() => {
              changeCurrentTab("googleSheets");
            }}
          >
            Google Sheets
          </TabPanel>
          {RcrmAppAvailableResult &&
          RcrmAppAvailableResult.result === 0 &&
          RcrmAppAvailableResult.data &&
          RcrmAppAvailableResult.data.foundApp ? (
            <TabPanel
              className={currentTab === "rcrm" ? "selected" : ""}
              onClick={() => {
                changeCurrentTab("rcrm");
              }}
            >
              R-CRM
            </TabPanel>
          ) : (
            ""
          )}
          <TabDummy></TabDummy>
        </Tabs>
        {availableForm.result === 0 || availableForm.result === 1 ? (
          <>
            {availableForm.result === 0 &&
              availableForm.msg === "GET_DATA_COMPLETED" &&
              availableForm.formStatus != "ALLUSED" && (
                <AddButtonContainer>
                  <Button onClick={() => handleSettingModal(currentTab, "add")}>{t("__add_connection")}</Button>
                </AddButtonContainer>
              )}
            {(availableForm.result === 1 || (availableForm.result === 0 && availableForm.msg === "NO_DATA")) && (
              <AddButtonContainer>
                <Button onClick={() => handleSettingModal(currentTab, "add")}>{t("__add_connection")}</Button>
              </AddButtonContainer>
            )}
            {Card()}
          </>
        ) : (
          <LoadingArea>
            <StyledLoadingIcon></StyledLoadingIcon>
          </LoadingArea>
        )}
        <Toast
          show={showSuccessToast}
          message={t("__save_success")}
          bgColor="#4CAF50"
          callback={successToastCallback}
        ></Toast>
        <Toast
          show={showFailedToast}
          message={t("__save_failed")}
          bgColor="#F44336"
          callback={failedToastCallback}
        ></Toast>
        <Modal state={modalStatusDisplay} content={modalStatusContent()} footer={modalStatusFooter()} />
        <Toast
          show={showDeleteSuccessToast}
          message={t("__delete_success")}
          bgColor="#4CAF50"
          callback={deleteSuccessToastCallback}
        ></Toast>
        <Toast
          show={showDeleteFailedToast}
          message={t("__delete_failed")}
          bgColor="#F44336"
          callback={deleteFailedToastCallback}
        ></Toast>
        <Modal
          state={modalConfirmDeleteDisplay}
          content={modalConfirmDeleteContent()}
          footer={modalConfirmDeleteFooter()}
        />
        <SettingModal
          show={modalSettingDisplay}
          setShow={setModalSettingDisplay}
          status={editStatus}
          currentTab={currentTab}
        />
      </LeadFormContainer>
    </>
  );
};

export default LeadForms;

const LeadFormContainer = styled.div`
  position: relative;
`;

const SetupHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const SetupTextHeader = styled(TextHeader)`
  font-size: 18px;
  margin-bottom: 10px;
`;

const TextHeaderDescription = styled.div`
  font-size: 14px;
  color: #999;
`;

const StyledManualLink = styled.div`
  text-align: right;
  margin-bottom: 10px;
`;

const StyledLink = styled.a`
  text-decoration: none;
  color: #337ab7;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
`;

const Tabs = styled.div`
  margin-top: 10px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding-left: 0px;
  border-bottom: 1px solid #eee;
`;

const TabPanel = styled.div`
  min-width: 100px;
  max-width: 50%;
  padding: 10px;
  list-style: none;
  text-align: center;
  cursor: pointer;

  &.selected {
    border-bottom: 2px solid #0088e3;
  }
`;

const TabDummy = styled.div`
  flex-grow: 1;
`;

const AddButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  aligh-items: center;
  margin-top: 20px;
`;

const MainCardContainer = styled.div`
  display: block;
`;

const CardBoxContainer = styled.div.attrs((props) => ({
  borderColor:
    props.ggAdsStatus === "error" ||
    props.ggStatus === "error" ||
    props.crmStatus === "error" ||
    props.googleAdsChangeStatus
      ? colors.negative
      : "#eee",
}))`
  border: 1px solid ${(props) => props.borderColor};
  border-radius: 8px;
  margin-top: 20px;
`;

const CardBoxContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardBoxHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 10px 20px 0px 20px;
  align-items: center;
  height: 40px;
`;

const AccountArea = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0px 10px 10px 0px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

const HeaderImage = styled.img`
  border-radius: 50%;
  width: 40px;
  height: 40px;
  align-self: center;
  margin-right: 10px;
  vertical-align: middle;
`;

const AccountText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  .google-ads-name {
    white-space: wrap;
    word-break: break-word;
  }
  .google-ads-id {
    font-size: 12px;
    color: #999;
    white-space: wrap;
    word-break: break-word;
  }
`;

const HeaderAction = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;
`;

const CardBoxBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const GoogleAdsArea = styled.div.attrs((props) => ({
  margin: props.status !== null ? "10px 20px 10px 5px" : "20px 20px 10px 90px",
  height: props.status === null ? "40px" : "fit-content",
  display: props.status === null ? "flex" : "block",
  justifyContent: props.status === null ? "center" : null,
  alignItems: props.status === null ? "center" : null,
}))`
  margin: ${(props) => props.margin};
  height: ${(props) => props.height};
  display: ${(props) => props.display};
  justify-content: ${(props) => props.justifyContent};
  align-items: ${(props) => props.alignItems};
`;

const FormListContainer = styled.div`
  margin: 10px 10px 10px 105px;
  list-style-position: inside;
  font-size: 14px;
  .item-list {
    text-indent: -1.2rem;
  }
  .form-name {
    white-space: wrap;
    word-break: break-word;
  }
  .form-id {
    font-size: 12px;
    color: #999;
    margin-left: 20px;
  }
  & > *:not(:first-child) {
    margin-top: 5px;
  }
`;

const FileArea = styled.div.attrs((props) => ({
  margin: props.status !== null ? "10px 20px 10px 5px" : "20px 20px 20px 90px",
  height: props.status === null ? "40px" : "fit-content",
  flexDirection: props.status === null ? "" : "row",
  justifyContent: props.status === null ? "center" : null,
  alignItems: props.status === null ? "center" : null,
}))`
  margin: ${(props) => props.margin};
  height: ${(props) => props.height};
  display: flex;
  flex-direction: ${(props) => props.flexDirection};
  justify-content: ${(props) => props.justifyContent};
  align-items: ${(props) => props.alignItems};
`;

const ErrorArea = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  margin: 5px 0;
  font-size: 12px;
  .error-message {
    color: ${colors.negative};
  }
  .error-edit-link {
    color: ${colors.negative};
    cursor: pointer;
    margin-left: 5px;
    text-decoration: underline;
  }
`;

const ErrorIconArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  margin-right: 15px;
  .tooltip-style {
    border-radius: 4px;
    opacity: 1 !important;
    max-width: 400px;
    line-height: 1.5rem;
  }
`;

const ErrorIconTargetArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  margin-right: 5px;
  .tooltip-style {
    border-radius: 4px;
    opacity: 1 !important;
    max-width: 400px;
    line-height: 1.5rem;
  }
`;

const ErrorIcon = styled(ExclamationCircleFill).attrs((props) => ({
  color: props.color,
}))`
  color: ${(props) => props.color};
  width: 16px;
  height: 16px;
`;

const GateArea = styled.div.attrs((props) => ({
  margin: props.status !== null ? "10px 20px 10px 5px" : "20px 20px 20px 90px",
  height: props.status === null ? "40px" : "fit-content",
  flexDirection: props.status === null ? "" : "row",
  justifyContent: props.status === null ? "center" : null,
  alignItems: props.status === null ? "center" : null,
}))`
  margin: ${(props) => props.margin};
  height: ${(props) => props.height};
  display: flex;
  flex-direction: ${(props) => props.flexDirection};
  justify-content: ${(props) => props.justifyContent};
  align-items: ${(props) => props.alignItems};
`;

const ConnectButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  aligh-items: center;
  height: 40px;
`;

const CardBoxFooter = styled.div`
  padding: 0px;
  margin: 0px;
  padding: 0px 20px 10px 20px;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  font-size: 12px;
  justify-content: flex-end;
  margin-top: 10px;
  color: #999;
`;

const LastEditedTime = styled.div``;

const LastEditedBy = styled.div`
  display: flex;
  flex-direction: row;
  .edit-by {
    padding-left: 5px;
    margin-right: 5px;
  }
`;

const ProfileImage = styled.img`
  position: relative;
  top: 1px;
  border-radius: 50%;
  width: 15px;
  height: 15px;
`;

const LastEditedName = styled.div`
  margin-left: 3px;
`;

const LoadingArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  height: 225px;
`;

const StyledLoadingIcon = styled.div`
  border: 5px solid #ccc;
  border-top: 5px solid #eee;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;
  position: sticky;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

//more options menu
const ItemsContainer = styled.div.attrs((props) => ({
  display: props.isVisible ? "block" : "none",
  top: props.position ? props.position.top : "35px",
  right: props.position && props.position.right ? props.position.right : null,
  minWidth: props.minWidth,
}))`
  display: ${(props) => props.display};
  position: absolute;
  border: 1px solid #f0f0f0;
  background-color: ${colors.backgroundSection};
  box-shadow: ${colors.sectionShadow} 0 10px 30px;
  min-width: 150px;
  border-radius: 8px;
  z-index: 999;
  top: ${(props) => props.top};
  right: ${(props) => props.right};
  min-width: ${(props) => props.minWidth};
  padding: 16px 0;
  text-align: left;
  max-height: 170px;
  overflow: auto;

  @media screen and (max-width: 834px) {
    box-shadow: none;
    width: calc(100% - 32px);
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    margin: 16px;
    text-align: center;
    max-height: calc(100vh - 150px);
    min-width: unset;

    p {
      font-size: 14px;
    }
  }
`;

const StyledItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;

  @media screen and (max-width: 834px) {
    padding: 16px;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 998;

  @media screen and (max-width: 834px) {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const IconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  > *:first-child {
    cursor: pointer;
  }
`;

const StyleThreeDotsVertical = styled(ThreeDotsVertical)`
  color: #aaa;
  cursor: pointer;
  width: 16px;
  height: 16px;
  text-align: right;
`;

const MenuIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 5px;
`;

//modal warning status
const ModalContentContainer = styled.div`
  text-align: center;
  margin-bottom: 20px;
  color: #575757;
  font-weight: bold;

  .info-icon {
    font-size: 78px;
    color: #f8bb86;
    margin-bottom: 20px;
    font-weight: normal;
  }
  .content {
    padding: 0 20px;
  }
  .sub-content {
    padding: 0 20px 20px 20px;
  }
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 16px;

  > Button {
    min-width: 100px;
    margin-bottom: 0px;
  }

  > Button:first-child {
    margin-right: 10px;
  }
`;

const HelpIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 6px;
`;
